// Sheet ↔ audio bar mapping for a PDF score (pure).
//  1. playing order: unroll repeats — at :‖ jump back to the last ‖: (or the start / the bar after the
//     previous repeat), once; on the second pass a "1." volta is skipped in favour of "2."
//  2. a multi-measure rest box stands for `span` bars
//  3. where the sheet starts in the audio:
//     - sheets with chord symbols: the offset whose chords agree best with the analysed chord of each
//       bar (an arrangement often has a shorter/longer intro than the recording)
//     - otherwise: where the music core starts (a video's skit/talk intro isn't on the sheet); without
//       that, skip the analysis' short pickup bar (beat tracking often starts a beat early)
//  then played bar i ↔ audio bar i + offset.

export interface SheetBox { span?: number; repeatStart?: boolean; repeatEnd?: boolean; ending?: number; chords?: string[] }

export interface SheetMap {
  /** box to highlight while audio bar `a` plays, or null (pickup / past the sheet) */
  boxForAudio(a: number): number | null;
  /** audio bar to jump to when box `i` is tapped: the pass at/after audio bar `from`, else the first pass */
  audioForBox(i: number, from?: number): number | null;
  /** bars in playing order (repeats unrolled, multi-rests expanded) */
  sheetBars: number;
  /** audio bar of the sheet's first played bar, and how it was found */
  offset: number;
  offsetFrom: "chords" | "music" | "pickup";
}

const PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
/** pitch class of a chord's root ("E♭" = "D#" = 3); null for "/F", "N.C." … */
export function chordRoot(sym: string | null | undefined): number | null {
  const m = sym ? /^([A-G])([#♯b♭]?)/.exec(sym) : null;
  if (!m) return null;
  const acc = m[2] === "#" || m[2] === "♯" ? 1 : m[2] === "b" || m[2] === "♭" ? -1 : 0;
  return (PC[m[1]] + acc + 12) % 12;
}

/** audio offset whose per-bar chord roots agree best with the sheet's (null when it can't tell) */
export function chordOffset(sheet: (number | null)[], audio: (number | null)[], maxOffset = 24): { offset: number; score: number } | null {
  let best: { offset: number; score: number; n: number } | null = null;
  for (let o = -8; o <= maxOffset; o++) {
    let n = 0, hit = 0;
    for (let i = 0; i < sheet.length; i++) {
      const a = audio[i + o], s = sheet[i];
      if (a == null || s == null) continue;
      n++; if (a === s) hit++;
    }
    if (n < 8) continue;
    const score = hit / n;
    if (!best || score > best.score + 1e-9) best = { offset: o, score, n };
  }
  return best && best.score >= 0.5 ? { offset: best.offset, score: best.score } : null;
}

/** box indices in playing order (repeats unrolled) */
export function playingOrder(boxes: SheetBox[]): number[] {
  const n = boxes.length, order: number[] = [];
  const repeated = new Set<number>();          // :‖ boxes already jumped back from
  let start = 0;                               // where the current repeat section begins
  let second = false;                          // inside the second pass of a section
  for (let i = 0, guard = 0; i < n && guard < n * 4; guard++) {
    const b = boxes[i];
    if (b.repeatStart && !second) start = i;
    if (second && b.ending === 1) {            // second pass: skip the 1st ending, go to the 2nd
      let j = i + 1;
      while (j < n && !((boxes[j].ending ?? 0) >= 2)) j++;
      second = false;
      if (j < n) { i = j; start = j; continue; }
    }
    order.push(i);
    if (b.repeatEnd && !repeated.has(i)) { repeated.add(i); second = true; i = start; continue; }
    if (b.repeatEnd) { second = false; start = i + 1; }   // section done; a lone :‖ later repeats from here
    i++;
  }
  return order;
}

export function buildSheetMap(boxes: SheetBox[], audioBarBeats: number[], beatsPerBar: number, audioChords?: (string | null)[], musicStartBar = 0): SheetMap {
  const barBox: number[] = [];                       // played bar → box
  const barChord: (number | null)[] = [];            // played bar → chord root printed at its start
  const passStart: { box: number; bar: number }[] = [];   // each played occurrence of a box
  for (const i of playingOrder(boxes)) {
    passStart.push({ box: i, bar: barBox.length });
    for (let k = 0; k < Math.max(1, boxes[i].span ?? 1); k++) {
      barBox.push(i);
      barChord.push(k === 0 ? chordRoot(boxes[i].chords?.[0]) : null);
    }
  }
  const byChords = audioChords && barChord.filter((c) => c != null).length >= 8
    ? chordOffset(barChord, audioChords.map(chordRoot), Math.max(24, musicStartBar + 8)) : null;
  const pickup = audioBarBeats.length > 1 && audioBarBeats[0] < beatsPerBar ? 1 : 0;
  const offsetFrom: SheetMap["offsetFrom"] = byChords ? "chords" : musicStartBar > pickup ? "music" : "pickup";
  const offset = byChords ? byChords.offset : Math.max(musicStartBar, pickup);
  return {
    sheetBars: barBox.length,
    offset,
    offsetFrom,
    boxForAudio(a) {
      const s = a - offset;
      return a >= 0 && s >= 0 && s < barBox.length ? barBox[s] : null;
    },
    audioForBox(i, from = 0) {
      const passes = passStart.filter((p) => p.box === i).map((p) => p.bar + offset).filter((a) => a >= 0 && a < audioBarBeats.length);
      if (!passes.length) return null;
      return passes.find((a) => a >= from) ?? passes[0];
    },
  };
}
